import React, { useEffect } from 'react';
import { DateTime } from 'luxon';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import {
  DailyReportDetailSectionDocument,
  DailyReportDetailSectionQuery,
  SaveIncident_Input,
  SaveIncidentMutation,
  useIncidentFormQuery,
  useSaveIncidentMutation
} from 'generated/graphql';
import _ from 'lodash';

export type IFormState = {
  description: string;
  id: string;
  personsInvolvedIds: string[];
  projectId: string;
  issueDate: string;
};

const initialState: IFormState = {
  description: '',
  id: '',
  personsInvolvedIds: [],
  projectId: '',
  issueDate: DateTime.now().toFormat('yyyy-LL-dd')

};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      description: {
        label: 'Description',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      personsInvolvedIds: {
        label: 'Persons involved ids',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      issueDate: {
        label: 'Date of Incident',
        type: 'date'
      }
    }
  });


  useEffect(() => {
    if (formState.projectId === '' && projectId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
    return () => {};
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, called, loading } = useQuery(id);

  useEffect(() => {
    if (data.id !== '' && formState.id === '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
    return () => {};
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);


  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveIncidentForm() {
  const [
    fnMutate,
    tuple
  ] = useSaveIncidentMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveIncident_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<DailyReportDetailSectionQuery, SaveIncidentMutation>(
            DailyReportDetailSectionDocument, (prevState, { mutationResult }) => {
              if (!prevState.dailyReport?.id) {
                return prevState;
              }
              if (!mutationResult.data?.saveIncident?.id) {
                return prevState;
              }

              // existing
              if (input.id === mutationResult.data.saveIncident.id) {
                const updatedIncidents = prevState.dailyReport?.incidents.map((rec) => {
                  if (rec.id === mutationResult.data?.saveIncident?.id) {
                    return {
                      ...rec,
                      ...mutationResult.data.saveIncident
                    };
                  }
                  return rec;
                });
                return {
                  ...prevState,
                  dailyReport: {
                    ...prevState.dailyReport,
                    incidents: _.compact(updatedIncidents)
                  }
                };
              }

              return {
                ...prevState,
                dailyReport: {
                  ...prevState.dailyReport,
                  incidents: _.compact([
                    ...prevState.dailyReport?.incidents ?? [],
                    mutationResult.data.saveIncident
                  ])
                }
              };
            }
          )
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export interface IData extends IFormState {}

export function useQuery(id: string) {
  const tuple = useIncidentFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.incident?.id ?? '',
        description: tuple.data?.incident?.description ?? '',
        issueDate: tuple.data?.incident?.issueDate.toFormat('yyyy-LL-dd') ?? DateTime.now().toFormat('yyyy-LL-dd'),
        personsInvolvedIds: tuple.data?.incident?.personsInvolved.map((node) => node.id) ?? [],
        projectId: tuple.data?.incident?.project.id ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
